import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { ISocialLink } from '@core/models';
import { Page } from '@core/services';
import { TranslateModule } from '@ngx-translate/core';
import { CdnUrlPipe } from '@shared/pipes';
import { LazyLoadImageModule } from 'ng-lazyload-image';

/**
 * Footer component responsible for rendering the footer section
 * that appears on the main page and throughout the application.
 *
 * @selector <app-footer>
 */
@Component({
  selector: 'app-footer',
  imports: [TranslateModule, RouterModule, LazyLoadImageModule, CdnUrlPipe],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Footer {
  /** Permite ocultar la franja de aliados cuando la página ya presenta una propia. */
  readonly showPartners = input<boolean>(true);

  /**
   * Service for sanitizing HTML content.
   */
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Service for handling page data.
   */
  private readonly pageService = inject(Page);

  /**
   * Signal containing the home page information.
   */
  protected readonly home = this.pageService.signalHome;

  /**
   * Signal containing sanitized HTML for the footer "about" section.
   */
  protected readonly footerAbout = computed<SafeHtml>(() => {
    const home = this.home();
    if (!home) return this.sanitizer.bypassSecurityTrustHtml('');
    return this.sanitizer.bypassSecurityTrustHtml(home.home.language.footer_about);
  });

  /**
   * Signal containing the list of social links for the footer.
   */
  protected readonly socialLinks = computed<ISocialLink[]>(() => {
    const home = this.home();
    if (!home) return [];

    if (!home.home?.language?.social_links) return [];

    return Object.values(home.home.language.social_links);
  });

  /**
   * Año que se muestra en la línea de copyright.
   */
  protected readonly currentYear = new Date().getFullYear();

  get sortedLogos() {
    return [...(this.home()?.home?.language?.banner_logo || [])].sort((a, b) => a.order - b.order);
  }
}
